import * as Sentry from '@sentry/react';
import React, { ComponentType, FC, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Redirect,
  Route,
  Router,
  RouteComponentProps,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { Toaster } from 'react-hot-toast';

import { setCurrentUser } from '../actions/action_auth';
import { setCurrentLocation } from '../actions/action_location';
import { getParentData, getStudents } from '../actions/action_parent';
import OrderRouter from './v3/productConfig';

// React.js components
import ParentChangeAddress from './parents/parent_change_address';
import ParentChangeEmail from './parents/parent_change_email';
import ParentChangePhone from './parents/parent_change_phone';
import ParentChangeName from './parents/parent_change_name';
import ParentChangePassword from './parents/parent_change_password';
import ParentAccountAddStudent from './parents/parent_account_add_student';
import ParentConfirmEmail from './parents/parent_confirm_email';
import ParentViewStudent from './parents/parent_view_student';
import ParentEditStudent from './parents/parent_edit_student';

import LoginRedirect from './auth/LoginRedirect';
import NewParentSignupFormContainer from './auth/NewParentSignupFormContainer';
import NewPasswordLookupForm from './auth/NewPasswordLookupForm';
import ParentAuthContainer from './auth/parent_auth_container';
import AccountLookupForm from './auth/parent_lookup_form';
import ParentSignupFormContainer from './auth/parent_signup_form_container';
import Navbar from './navbar/navbar';
import BnlParentClickAndSafe from './parents/bnl/bnl_parent_click_and_safe';
import BnlParentStudentPreShootDetailForm from './parents/bnl/bnl_parent_student_preshoot_detail_form';
import BnlStudentSelection from './parents/bnl/bnl_parent_student_selection';
import ParentAddStudentForm from './parents/parent_add_student_form';
import ParentMyAccount from './parents/parent_my_account';
import MyVouchersPage from './parents/MyVouchersPage';
import MyOrdersPage from './parents/orders/MyOrdersPage';
import OrderDetailPage from './parents/orders/OrderDetailPage';
import Notification from './utils/notification';
import Cart from './v3/cart/Cart';
import AbandonedCartRouteGate from './v3/cart/AbandonedCartRouteGate';
import CartRoute from './v3/cart/CartRoute';
import Category from './v3/categories/Category';
import Offerings from './v3/categories/Offerings';
import Checkout from './v3/checkout/Checkout';
import CustomerAddressEdit from './v3/checkout/subcomponents/CustomerAddressEdit';
import CustomerEmailEdit from './v3/checkout/subcomponents/CustomerEmailEdit';
import CustomerPhoneEdit from './v3/checkout/subcomponents/CustomerPhoneEdit';
import IncentiveOffers from './v3/incentives/IncentiveOffers';
import PersonalizeProduct from './v3/products/personalization/PersonalizeProduct';
import RetouchingConfig from './v3/products/retouching/RetouchingConfig';
import SentryTesting from './v3/utils/SentryTesting';
import VenueLookupByZipcode from './venue-lookup/VenueLookupByZipcode';
/* TODO: remove these testing components when testing is complete */
import ParentDashboardRoute from '../routes/ParentDashboardRoute';
import PrivateCheckoutRoute from '../routes/PrivateCheckoutRoute';
import PrivateRoute from '../routes/PrivateRoute';
import PrivateRouteWithV3Layout from '../routes/PrivateRouteWithV3Layout';
import NthFreeQualification from './utils/NthFreeQualification';
import PreCartOffers from './utils/PreCartOffers';
import ScrollToTop from './utils/ScrollToTop';
import CustomerAddressConfirmation from './v3/address/CustomerAddressConfirmation';
import CheckoutSuccess from './v3/checkout/CheckoutSuccess';
import ClearConfig from './v3/hooks/clearConfig';
import LookupByVenueAndStudentId from './v3/lookups/LookupByVenueAndStudentId';
import ProductConfig from './v3/products/ProductConfig';
import AdditionalOfferProducts from './v3/products/additional-offers/AdditionalOffers';
import IncentivesConfig from './v3/products/incentive/IncentivesConfig';
import InterstitialConfig from './v3/products/interstitial/InterstitialConfig';
import PersonalizationOffer from './v3/products/personalization/PersonalizationOffer';
import PreCartOfferConfig from './v3/products/pre-cart/PreCartOfferConfig';
import SuggestedProducts from './v3/products/suggested/SuggestedProducts';
import TermsConditions from './v3/terms/TermsConditions';
import AllRegistrations from './event-registration/AllRegistrations';
import EventRegistrationPage from './event-registration/EventRegistrationPage';
import UnsubscribeMessage from './v3/unsubscribe/UnsubscribeMessage';
import UnsubscribeReasons from './v3/unsubscribe/UnsubscribeReasons';
import DashboardRedirect from './v3/utils/DashboardRedirect';
import SelectYearbookBackground from './v3/yearbook/SelectYearbookBackground';
import SelectYearbookComplete from './v3/yearbook/SelectYearbookComplete';
import SelectYearbookPhoto from './v3/yearbook/SelectYearbookPhoto';
import VoucherPurchase from './v3/vouchers/VoucherPurchase';
import VoucherPurchaseSuccess from './v3/vouchers/VoucherPurchaseSuccess';
import VoucherProductDetail from './v3/vouchers/VoucherProductDetail';
import SentrySourceMapTest from './SentrySourceMapTest';

// Only the studio config spread in from the server-rendered #config-data
// payload in packs/index.js — which is why these are snake_case. Router and
// store values come from hooks now rather than withRouter/connect.
interface AppProps {
  css?: string;
  auth_token?: string;
  event_lookup_by_zipcode_enabled?: boolean;
}

// navbar is a legacy connect() + withRouter component in plain JS, so its
// inferred props stop at what those inject. State the contract it is actually
// given here rather than erasing it.
const BrandNavbar = Navbar as unknown as ComponentType<{
  companyName: string;
  history: RouteComponentProps['history'];
}>;

const App: FC<AppProps> = ({
  css,
  auth_token: companyAuthToken,
  event_lookup_by_zipcode_enabled: eventLookupByZipcodeEnabled,
}) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>();
  // Two primitive reads rather than one selector returning an object: a fresh
  // object defeats useSelector's reference equality and would re-render App on
  // every dispatched action — the churn this PR exists to remove.
  const currentUserId = useSelector(
    (state: { auth?: { currentUserId?: number | null } }) =>
      state.auth?.currentUserId ?? null,
  );
  const currentUserEmail = useSelector(
    (state: { auth?: { currentUserEmail?: string | null } }) =>
      state.auth?.currentUserEmail ?? null,
  );

  // Mount only, matching the componentDidMount this replaced.
  useEffect(() => {
    // TODO: can we remove this??
    // redux-promise resolves the payload before the action reaches the
    // reducer, so dispatching this returns the settled action, not the action
    // creator's declared return value.
    //
    // The class version read the CSRF meta tag and passed it here, but
    // setCurrentUser takes no arguments and never did — it just GETs
    // /api/v2/current-user. Typing the dispatch surfaced that, so the dead
    // jQuery read is gone with it.
    const currentUser = dispatch(setCurrentUser()) as unknown as Promise<{
      payload?: { data?: { currentUserId?: number } };
    }>;

    currentUser.then((response) => {
      if (response?.payload?.data) {
        if (response.payload.data.currentUserId) {
          dispatch(getParentData());
          dispatch(getStudents(response.payload.data.currentUserId));
        }
      }
    });

    Sentry.setUser({
      email: currentUserEmail ?? undefined,
      id: currentUserId ?? undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // componentDidUpdate only fired on a location *change*, never on mount, and
  // the store's initial location stays unset until the first navigation. The
  // ref preserves that: dropping it would dispatch the entry path on mount,
  // which is a behaviour change and not this PR's to make.
  const isFirstLocation = useRef(true);
  useEffect(() => {
    if (isFirstLocation.current) {
      isFirstLocation.current = false;
      return;
    }

    dispatch(setCurrentLocation(location.pathname));
  }, [location, dispatch]);

  const companyName = css;

  if (!companyName) return <div className="loading" />;

  // TODO: remove test routes when testing is complete
  return (
    <Router history={history}>
      <div id="app" className={`app--${companyName}`}>
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 3000,
            success: {
              style: {
                background: '#22c55e',
                color: '#fff',
              },
            },
          }}
        />
        <Notification />
        <ClearConfig />
        <Route
          render={(props) => (
            <BrandNavbar companyName={companyName} history={props.history} />
          )}
        />
        <ScrollToTop>
          <div id="react-component">
            <Route
              exact
              path="/login"
              render={(props) => (
                <ParentAuthContainer companyToken={companyName} {...props} />
              )}
            />
            <Route
              exact
              path="/login-redirect"
              render={(props) => <LoginRedirect {...props} />}
            />
            <Route
              exact
              path="/account-lookup"
              render={(props) => <AccountLookupForm {...props} />}
            />
            <Route
              exact
              path="/signup"
              render={(props) => <ParentSignupFormContainer {...props} />}
            />
            <Route
              exact
              path="/new-account"
              render={(props) => <NewParentSignupFormContainer {...props} />}
            />
            <Route
              exact
              path="/password-lookup"
              render={(props) => <NewPasswordLookupForm {...props} />}
            />
            <ParentDashboardRoute
              companyName={companyName}
              companyAuthToken={companyAuthToken}
              eventLookupByZipcodeEnabled={eventLookupByZipcodeEnabled}
            />
            <Route
              exact
              path="/unsubscribe-reasons"
              component={UnsubscribeReasons}
            />
            <Route
              exact
              path="/unsubscribe-message"
              render={(props) => <UnsubscribeMessage {...props} />}
            />
            <PrivateRoute
              exact
              path="/dashboard-redirect"
              component={DashboardRedirect}
            />
            <Route
              exact
              path="/events/:key/register"
              component={EventRegistrationPage}
            />
            <PrivateRoute
              exact
              path="/venues/lookup-by-zipcode"
              component={VenueLookupByZipcode}
            />
            <PrivateRoute
              exact
              path="/select-student"
              component={BnlStudentSelection}
            />
            <PrivateRoute
              exact
              path="/v3/lookup-by-student-id"
              component={LookupByVenueAndStudentId}
            />
            <PrivateRoute
              exact
              path="/preshoot-student-form"
              component={BnlParentStudentPreShootDetailForm}
            />
            <PrivateRoute
              exact
              path="/add-student"
              component={ParentAddStudentForm}
            />
            <PrivateRoute
              exact
              path="/my-account"
              component={ParentMyAccount}
            />
            <PrivateRoute
              exact
              path="/my-account/vouchers"
              component={MyVouchersPage}
            />
            <PrivateRoute
              exact
              path="/my-account/add-student"
              component={ParentAccountAddStudent}
            />
            <PrivateRoute
              exact
              path="/my-account/name"
              component={ParentChangeName}
            />
            <PrivateRoute
              exact
              path="/my-account/address"
              component={ParentChangeAddress}
            />
            <PrivateRoute
              exact
              path="/my-account/email"
              component={ParentChangeEmail}
            />
            <PrivateRoute
              exact
              path="/my-account/change-password"
              component={ParentChangePassword}
            />
            <PrivateRoute
              exact
              path="/my-account/phone"
              component={ParentChangePhone}
            />
            <PrivateRoute
              exact
              path="/my-account/confirm"
              component={ParentConfirmEmail}
            />
            <PrivateRoute
              exact
              path="/my-account/students/:id"
              component={ParentViewStudent}
            />
            <PrivateRoute
              exact
              path="/my-account/students/:id/edit"
              component={ParentEditStudent}
            />
            <PrivateRoute
              exact
              path="/my-account/registrations"
              component={AllRegistrations}
            />
            <PrivateRoute
              exact
              path="/click-and-safe"
              component={BnlParentClickAndSafe}
            />
            {/* Deliberately crashes — verifies Sentry source maps after a
                  deploy. See app/javascript/components/SentrySourceMapTest.jsx
                  and docs/sentry-source-maps.md. */}
            <Route
              exact
              path="/sentry-source-map-test"
              component={SentrySourceMapTest}
            />
            <PrivateRoute exact path="/my-orders" component={MyOrdersPage} />
            <PrivateRoute
              exact
              path="/my-orders/:orderId"
              component={OrderDetailPage}
            />
            <Route
              exact
              path="/order"
              render={() => <Redirect to="/dashboard" />}
            />
            <PrivateRoute
              exact
              path="/v3/yearbook-selection/:studentId"
              component={SelectYearbookPhoto}
            />
            <PrivateRouteWithV3Layout
              exact
              path="/v3/terms-conditions"
              component={TermsConditions}
            />
            <PrivateRoute
              exact
              path="/v3/yearbook-background-selection"
              component={SelectYearbookBackground}
            />
            <PrivateRoute
              exact
              path="/v3/yearbook-selection-complete"
              component={SelectYearbookComplete}
            />
            <PrivateRoute
              exact
              path="/v3/incentive-offers"
              component={IncentiveOffers}
            />
            <PrivateRouteWithV3Layout
              exact
              path="/v3/sentry-testing"
              component={SentryTesting}
            />
            <PrivateRouteWithV3Layout
              exact
              path="/v3/categories/:id"
              component={Category}
            />
            <Route
              exact
              path="/v3/offers"
              render={(props) => (
                <NthFreeQualification {...props}>
                  <PrivateRouteWithV3Layout exact component={Offerings} />
                </NthFreeQualification>
              )}
            />
            <PrivateRouteWithV3Layout
              path="/v3/products/:id"
              component={OrderRouter}
            />
            <PrivateRouteWithV3Layout
              exact
              path="/v3/suggested-products"
              component={SuggestedProducts}
            />
            <PrivateRouteWithV3Layout
              exact
              path="/v3/additional-offers"
              component={AdditionalOfferProducts}
            />
            <PrivateRouteWithV3Layout
              exact
              path="/v3/products-regular/:id"
              component={ProductConfig}
            />
            <Route
              exact
              path="/v3/cart"
              render={(props) => (
                <AbandonedCartRouteGate
                  key={props.location?.search || ''}
                  search={props.location?.search}
                  restoreRoute={
                    <PrivateRouteWithV3Layout
                      exact
                      component={CartRoute}
                      showStudentBanner={false}
                    />
                  }
                >
                  <NthFreeQualification {...props}>
                    <PreCartOffers {...props}>
                      <PrivateRouteWithV3Layout
                        exact
                        component={Cart}
                        showStudentBanner={false}
                      />
                    </PreCartOffers>
                  </NthFreeQualification>
                </AbandonedCartRouteGate>
              )}
            />
            <PrivateRouteWithV3Layout
              exact
              path="/v3/personalization"
              component={PersonalizeProduct}
            />
            <PrivateRouteWithV3Layout
              exact
              path="/v3/personalization-offer"
              component={PersonalizationOffer}
            />
            <PrivateRouteWithV3Layout
              exact
              path="/v3/incentives/config"
              component={IncentivesConfig}
            />
            <PrivateRouteWithV3Layout
              exact
              path="/v3/pre-cart-offers"
              component={PreCartOfferConfig}
            />
            <PrivateRouteWithV3Layout
              exact
              path="/v3/interstitial"
              component={InterstitialConfig}
            />
            <PrivateRouteWithV3Layout
              exact
              path="/v3/retouching"
              component={RetouchingConfig}
            />
            <PrivateCheckoutRoute
              exact
              path="/v3/checkout"
              component={Checkout}
              showStudentBanner={false}
            />
            <PrivateRouteWithV3Layout
              exact
              path="/v3/checkout/customer-address-confirmation"
              component={CustomerAddressConfirmation}
              showStudentBanner={false}
            />
            <PrivateRouteWithV3Layout
              exact
              path="/v3/checkout/customer-address-edit"
              component={CustomerAddressEdit}
              showStudentBanner={false}
            />
            <PrivateRouteWithV3Layout
              exact
              path="/v3/checkout/customer-email-edit"
              component={CustomerEmailEdit}
              showStudentBanner={false}
            />
            <PrivateRouteWithV3Layout
              exact
              path="/v3/checkout/customer-phone-edit"
              component={CustomerPhoneEdit}
              showStudentBanner={false}
            />
            {/* Deliberately public: Stripe redirects here after payment, and
                  parents can arrive with persisted auth missing (in-app
                  browsers, storage eviction). A PrivateRoute bounce destroys
                  the session_id/cart_uuid params and the order build waits for
                  the cron. The page renders no user data and the endpoint it
                  calls is unauthenticated. (ENG-2781) */}
            <Route
              exact
              path="/v3/checkout-success"
              component={CheckoutSuccess}
            />
            {/* Voucher purchase routes */}
            <PrivateRouteWithV3Layout
              exact
              path="/v3/voucher-products/:id"
              component={VoucherProductDetail}
              showStudentBanner={false}
            />
            <PrivateRouteWithV3Layout
              exact
              path="/vouchers/purchase"
              component={VoucherPurchase}
              showStudentBanner={false}
            />
            <PrivateRoute
              exact
              path="/vouchers/purchase/success"
              component={VoucherPurchaseSuccess}
            />
          </div>
        </ScrollToTop>
      </div>
    </Router>
  );
};

export default App;
